import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import CookieConsent from "@/components/CookieConsent";
import ChatWidget from "@/components/ChatWidget";
import Index from "./pages/Index.tsx";
import NotFound from "./pages/NotFound.tsx";
import Contact from "./pages/Contact.tsx";
import SolutionOtpAuthentication from "./pages/SolutionOtpAuthentication.tsx";

import SolutionA2P from "./pages/SolutionA2P.tsx";
import SolutionVoiceTermination from "./pages/SolutionVoiceTermination.tsx";
import SolutionSipTrunking from "./pages/SolutionSipTrunking.tsx";
import SolutionCallCenter from "./pages/SolutionCallCenter.tsx";
import SolutionSmsFirewall from "./pages/SolutionSmsFirewall.tsx";
import SolutionUserVerification from "./pages/SolutionUserVerification.tsx";
import SolutionTwoFactorAuthentication from "./pages/SolutionTwoFactorAuthentication.tsx";
import UseCaseOtpVerification from "./pages/UseCaseOtpVerification.tsx";
import UseCaseTransactionalAlerts from "./pages/UseCaseTransactionalAlerts.tsx";
import UseCasePlatformNotifications from "./pages/UseCasePlatformNotifications.tsx";
import UseCasePaymentFintech from "./pages/UseCasePaymentFintech.tsx";
import UseCasePromotionalSms from "./pages/UseCasePromotionalSms.tsx";
import Industries from "./pages/Industries.tsx";
import IndustriesDigitalMarketing from "./pages/IndustriesDigitalMarketing.tsx";
import IndustriesFintechPayments from "./pages/IndustriesFintechPayments.tsx";
import IndustriesFintechs from "./pages/IndustriesFintechs.tsx";
import IndustriesBanks from "./pages/IndustriesBanks.tsx";
import UseCaseFraudPrevention from "./pages/UseCaseFraudPrevention.tsx";
import UseCaseIdentityVerification from "./pages/UseCaseIdentityVerification.tsx";
import IndustriesIGaming from "./pages/IndustriesIGaming.tsx";
import IndustriesTelecomCpaas from "./pages/IndustriesTelecomCpaas.tsx";
import IndustriesRealEstate from "./pages/IndustriesRealEstate.tsx";
import IndustriesRetailEcommerce from "./pages/IndustriesRetailEcommerce.tsx";
import IndustriesEducationMarketing from "./pages/IndustriesEducationMarketing.tsx";
import CoverageRoutes from "./pages/CoverageRoutes.tsx";
import About from "./pages/About.tsx";
import Events from "./pages/Events.tsx";
import PrivacyPolicy from "./pages/PrivacyPolicy.tsx";
import Terms from "./pages/Terms.tsx";
import PlaceholderPage from "./pages/PlaceholderPage.tsx";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/solutions/otp-authentication" element={<SolutionOtpAuthentication />} />
          <Route path="/otp-verification" element={<Navigate to="/use-cases/otp-verification" replace />} />
          <Route path="/solutions/a2p-sms" element={<SolutionA2P />} />
          <Route path="/a2p-sms" element={<SolutionA2P />} />
          <Route path="/solutions/voice-termination" element={<SolutionVoiceTermination />} />
          <Route path="/solutions/sip-trunking" element={<SolutionSipTrunking />} />
          <Route path="/solutions/user-verification" element={<SolutionUserVerification />} />
          <Route path="/solutions/two-factor-authentication" element={<SolutionTwoFactorAuthentication />} />
          <Route path="/solutions/call-center-solutions" element={<SolutionCallCenter />} />
          <Route path="/use-cases/otp-verification" element={<UseCaseOtpVerification />} />
          <Route path="/use-cases/transactional-alerts" element={<UseCaseTransactionalAlerts />} />
          <Route path="/use-cases/platform-notifications" element={<UseCasePlatformNotifications />} />
          <Route path="/use-cases/payment-fintech" element={<UseCasePaymentFintech />} />
          <Route path="/use-cases/promotional-sms" element={<UseCasePromotionalSms />} />
          <Route path="/industries" element={<Industries />} />
          <Route path="/industries/digital-marketing-agencies" element={<IndustriesDigitalMarketing />} />
          <Route path="/industries/fintech-payments" element={<IndustriesFintechPayments />} />
          <Route path="/industries/igaming" element={<IndustriesIGaming />} />
          <Route path="/industries/telecom-cpaas" element={<IndustriesTelecomCpaas />} />
          <Route path="/industries/real-estate" element={<IndustriesRealEstate />} />
          <Route path="/industries/retail-ecommerce" element={<IndustriesRetailEcommerce />} />
          <Route path="/industries/education-marketing" element={<IndustriesEducationMarketing />} />
          <Route path="/coverage-routes" element={<CoverageRoutes />} />
          <Route path="/about" element={<About />} />
          <Route path="/events" element={<Events />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          {/* Placeholder pages for Explore Our Solutions links */}
          <Route path="/solutions/sms-firewall" element={<SolutionSmsFirewall />} />
          <Route path="/use-cases/identity-verification" element={<UseCaseIdentityVerification />} />
          <Route path="/use-cases/fraud-prevention" element={<UseCaseFraudPrevention />} />
          <Route path="/use-cases/marketing-campaigns" element={<PlaceholderPage title="Marketing Campaigns" path="/use-cases/marketing-campaigns" />} />
          <Route path="/industries/banks-financial-institutions" element={<IndustriesBanks />} />
          <Route path="/industries/fintechs" element={<IndustriesFintechs />} />
          <Route path="/industries/mnos-carriers" element={<PlaceholderPage title="MNOs & Carriers" path="/industries/mnos-carriers" />} />
          <Route path="/industries/enterprises" element={<PlaceholderPage title="Enterprises" path="/industries/enterprises" />} />
          {/* Legacy URL redirects (preserve SEO from old paths) */}
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/about_us" element={<Navigate to="/about" replace />} />
          <Route path="/contact_yoo" element={<Navigate to="/contact" replace />} />
          <Route path="/messaging_platform" element={<Navigate to="/solutions/a2p-sms" replace />} />
          <Route path="/sms" element={<Navigate to="/solutions/a2p-sms" replace />} />
          <Route path="/sms_api" element={<Navigate to="/solutions/a2p-sms" replace />} />
          <Route path="/sms_gateway" element={<Navigate to="/solutions/a2p-sms" replace />} />
          <Route path="/bulk" element={<Navigate to="/solutions/a2p-sms" replace />} />
          <Route path="/mass_texting" element={<Navigate to="/solutions/a2p-sms" replace />} />
          <Route path="/email_to_sms" element={<Navigate to="/solutions/a2p-sms" replace />} />
          <Route path="/rcs_messaging" element={<Navigate to="/solutions/a2p-sms" replace />} />
          <Route path="/viber_bulk" element={<Navigate to="/solutions/a2p-sms" replace />} />
          <Route path="/voice" element={<Navigate to="/solutions/voice-termination" replace />} />
          <Route path="/faq" element={<Navigate to="/" replace />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        <CookieConsent />
        <ChatWidget />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
